import type { ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { useResourceWithPollingQuery } from '@/features/resource/hooks/useResourceWithPollingQuery'
import type { Resource, ResourceCustomAttributeData, ResourceSteeringConfig } from '@/features/resource/types'

interface ResourceContextType {
  resource?: Resource
  steeringConfig?: ResourceSteeringConfig
  customAttributeData?: ResourceCustomAttributeData
  stopPolling: () => void
  restartPolling: () => void
}

const ResourceDataContext = createContext<ResourceContextType | null>(null)

export function ResourceDataProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { resourceUuid } = useParams()
  const { resource, steeringConfig, customAttributeData, stopPolling, restartPolling } = useResourceWithPollingQuery({
    id: resourceUuid!,
  })

  // Memoise so consumers using stopPolling/restartPolling as effect deps (notably ResourceCard's
  // deferred polling-restart timer) don't see a fresh function reference on every parent render.
  // The handlers themselves are stable via useCallback in useResourceWithPollingQuery.
  const value = useMemo(
    () => ({ resource, steeringConfig, customAttributeData, stopPolling, restartPolling }),
    [resource, steeringConfig, customAttributeData, stopPolling, restartPolling],
  )

  return <ResourceDataContext.Provider value={value}>{children}</ResourceDataContext.Provider>
}

export function useResourceData() {
  const context = useContext(ResourceDataContext)
  if (!context) {
    throw new Error('useResourceData must be used within ResourceDataProvider')
  }
  return context
}
